import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ClipboardList, Loader2, Send, WifiOff } from 'lucide-react';
import EmailEditor from '@/client/components/admin/Email/EmailEditor';
import { OUTLET_EMAIL_KIND_LABEL } from './components/OutletEmailDialog';
import { Button } from '@/components/ui/button';
import { MarketingPageShell } from './components/MarketingPageShell';
import { EmptyState, StatusBadge } from './components/marketing-ui';
import {
  useApproveContentMutation,
  useApprovePostMutation,
  useContentQuery,
  useOutletEmailsQuery,
  usePlacementsQuery,
  useSocialPostsQuery,
  useTransitionOutletEmailMutation,
  useTransitionPlacementMutation,
} from '@/client/api/marketing/studio';
import { usePlaysQuery } from '@/client/api/marketing/plays';
import { useMediaOutletsQuery } from '@/client/api/marketing/sources';
import type { ApiOutletEmail, ApiPlacement, ApiSocialPost } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  CONTENT_STATUS_BADGE,
  CONTENT_STATUS_LABEL,
  CONTENT_TYPE_LABEL,
  DISTRIBUTION_STATUS_BADGE,
  MEDIUM_LABEL,
  PLATFORM_LABEL,
  type ContentStatus,
  type ContentType,
  type DistributionStatus,
  type SocialPlatform,
  type TraditionalMedium,
} from '@/client/types/marketing';

/**
 * Crew → Approvals, on real data. The gate.
 *
 * There is no "approve as…" picker any more, and its absence is the point. The
 * server stamps the approver from the authenticated session and requires the
 * `marketing.approve` permission; letting the browser nominate who signed off
 * would make the stamp a claim rather than a record.
 *
 * For the same reason nothing here PATCHes `status`. It is excluded from the
 * writable fields server-side, so a status sent in an update is silently
 * dropped — approval happens through the approve endpoint or not at all.
 */

const ApprovalsPage = () => {
  const contentQ = useContentQuery({ limit: 100, status: 'in_review' });
  const postsQ = useSocialPostsQuery({ limit: 100, status: 'pending_approval' });
  const placementsQ = usePlacementsQuery({ limit: 100, status: 'pending_approval' });
  const emailsQ = useOutletEmailsQuery({ limit: 100, status: 'pending_approval' });
  const transitionEmail = useTransitionOutletEmailMutation();
  const [previewing, setPreviewing] = useState<number | null>(null);
  const pendingEmails = emailsQ.data?.items ?? [];
  const plays = usePlaysQuery({ limit: 100 });
  const outlets = useMediaOutletsQuery({ limit: 200 });

  const approveContent = useApproveContentMutation();
  const approvePost = useApprovePostMutation();
  const transitionPlacement = useTransitionPlacementMutation();

  const inReview = contentQ.data?.items ?? [];
  const pendingPosts = postsQ.data?.items ?? [];
  const pendingPlacements = placementsQ.data?.items ?? [];

  const playName = (id: number | null) =>
    plays.data?.items.find((p) => p.play_id === id)?.name ?? null;
  const outletName = (moid: number) =>
    outlets.data?.items.find((o) => o.moid === moid)?.name ?? `Outlet ${moid}`;

  const loading =
    contentQ.isLoading || postsQ.isLoading || placementsQ.isLoading || emailsQ.isLoading;
  const failed = contentQ.isError || postsQ.isError || placementsQ.isError || emailsQ.isError;
  const failure =
    (contentQ.isError && contentQ.error) ||
    (postsQ.isError && postsQ.error) ||
    (placementsQ.isError && placementsQ.error) ||
    (emailsQ.isError && emailsQ.error);

  const stats = useMemo(
    () => [
      {
        key: 'content',
        label: 'Content in review',
        value: loading ? 0 : inReview.length,
      },
      { key: 'posts', label: 'Posts pending', value: loading ? 0 : pendingPosts.length },
      {
        key: 'placements',
        label: 'Placements pending',
        value: loading ? 0 : pendingPlacements.length,
      },
      { key: 'emails', label: 'Outlet emails pending', value: loading ? 0 : pendingEmails.length },
    ],
    [inReview.length, pendingPosts.length, pendingPlacements.length, pendingEmails.length, loading],
  );

  const doApproveContent = (mciid: number, title: string) =>
    approveContent.mutate(
      { mciid },
      {
        onSuccess: () => toast.success(`Approved “${title}”`),
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, 'Could not approve — you may not hold marketing.approve'),
          ),
      },
    );

  const doApprovePost = (post: ApiSocialPost) =>
    approvePost.mutate(
      { mspid: post.mspid },
      {
        onSuccess: () =>
          toast.success(
            post.scheduled_at
              ? `Approved — scheduled for ${new Date(post.scheduled_at).toLocaleString()}`
              : 'Approved — queued for the next publish tick',
          ),
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, 'Could not approve — you may not hold marketing.approve'),
          ),
      },
    );

  const doApprovePlacement = (placement: ApiPlacement) =>
    transitionPlacement.mutate(
      { mtpid: placement.mtpid, action: 'approve' },
      {
        onSuccess: () => toast.success('Placement approved — ready to pitch'),
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, 'Could not approve — you may not hold marketing.approve'),
          ),
      },
    );

  const doApproveEmail = (email: ApiOutletEmail) =>
    transitionEmail.mutate(
      { meid: email.meid, action: 'approve' },
      {
        onSuccess: (res) =>
          res?.data?.status === 'sent'
            ? toast.success(`Sent to ${email.to_email}`)
            : toast.error(`Not sent: ${res?.data?.fail_reason ?? 'the send failed'}`),
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, 'Could not approve — you may not hold marketing.approve'),
          ),
      },
    );

  return (
    <MarketingPageShell
      title="Approvals"
      description="Content in review plus social, traditional and outlet-email items awaiting sign-off. For WIP comments and check-requests, use Feedback."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Crew' },
        { label: 'Approvals' },
      ]}
      icon={ClipboardList}
      stats={stats}
    >
      <p className="mb-4 text-sm text-gray-600">
        Approving stamps you as the approver, from your session — there is no “approve as someone
        else”. Looking for open comments?{' '}
        <Link
          to="/admin/marketing/crew/feedback"
          className="font-medium text-blue-700 hover:underline"
        >
          Crew → Feedback
        </Link>
        .
      </p>

      {loading && (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading the approval queue…
        </div>
      )}

      {!loading && failed && (
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load the approval queue</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(failure, 'The marketing API did not respond')}
          </div>
        </div>
      )}

      {!loading && !failed && (
        <>
          <section className="mb-8">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Content in review</h2>
            {inReview.length === 0 ? (
              <EmptyState label="No content waiting for review." />
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Title</th>
                      <th className="px-4 py-3 font-medium">Play</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Submitted</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {inReview.map((item) => (
                      <tr key={item.mciid} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{item.title}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {playName(item.play_id) ?? <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {CONTENT_TYPE_LABEL[item.content_type as ContentType] ??
                            item.content_type}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            label={
                              CONTENT_STATUS_LABEL[item.status as ContentStatus] ?? item.status
                            }
                            className={CONTENT_STATUS_BADGE[item.status as ContentStatus] ?? ''}
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {item.submitted_at
                            ? new Date(item.submitted_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1.5">
                            <Button asChild size="sm" variant="outline">
                              <Link to={`/admin/marketing/studio/content/${item.mciid}`}>
                                Open editor
                              </Link>
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="gap-1.5"
                              disabled={approveContent.isPending}
                              onClick={() => doApproveContent(item.mciid, item.title)}
                            >
                              <Check className="h-3.5 w-3.5" /> Approve
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="mb-8">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">
              Social posts pending approval
            </h2>
            {pendingPosts.length === 0 ? (
              <EmptyState label="No social posts pending approval." />
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Post</th>
                      <th className="px-4 py-3 font-medium">Play</th>
                      <th className="px-4 py-3 font-medium">Platform</th>
                      <th className="px-4 py-3 font-medium">Goes out</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pendingPosts.map((post) => (
                      <tr key={post.mspid} className="hover:bg-gray-50">
                        <td className="max-w-md px-4 py-3">
                          <p className="line-clamp-2 font-medium text-gray-900">
                            {post.caption ?? '(no caption)'}
                          </p>
                          <p className="text-xs text-gray-400">{post.handle}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {playName(post.play_id) ?? <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {PLATFORM_LABEL[post.platform as SocialPlatform] ?? post.platform}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {/* Approving does not publish. It schedules, or queues
                              for the next tick — say which, before they click. */}
                          {post.scheduled_at ? (
                            new Date(post.scheduled_at).toLocaleString()
                          ) : (
                            <span className="text-gray-400">next publish tick</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            label={post.status}
                            className={DISTRIBUTION_STATUS_BADGE[post.status] ?? ''}
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            type="button"
                            size="sm"
                            className="gap-1.5"
                            disabled={approvePost.isPending}
                            onClick={() => doApprovePost(post)}
                          >
                            <Check className="h-3.5 w-3.5" /> Approve
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold text-gray-900">
              Traditional placements pending approval
            </h2>
            {pendingPlacements.length === 0 ? (
              <EmptyState label="No traditional placements pending approval." />
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Outlet</th>
                      <th className="px-4 py-3 font-medium">Play</th>
                      <th className="px-4 py-3 font-medium">Medium</th>
                      <th className="px-4 py-3 font-medium">Target date</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pendingPlacements.map((placement) => (
                      <tr key={placement.mtpid} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {outletName(placement.moid)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {playName(placement.play_id) ?? <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {MEDIUM_LABEL[placement.medium as TraditionalMedium] ?? placement.medium}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {placement.scheduled_at
                            ? new Date(placement.scheduled_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            label={placement.status}
                            className={
                              DISTRIBUTION_STATUS_BADGE[
                                placement.status as DistributionStatus
                              ] ?? ''
                            }
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            type="button"
                            size="sm"
                            className="gap-1.5"
                            disabled={transitionPlacement.isPending}
                            onClick={() => doApprovePlacement(placement)}
                          >
                            <Check className="h-3.5 w-3.5" /> Approve
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
                  Approving a placement clears it to pitch. Whether it runs is the outlet’s call —
                  mark it published from Studio → Traditional once it does.
                </div>
              </div>
            )}
          </section>

          <section className="mt-8">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">
              Outlet emails pending approval
            </h2>
            {pendingEmails.length === 0 ? (
              <EmptyState label="No outlet emails pending approval." />
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Outlet</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Submitted</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pendingEmails.map((email) => (
                      <Fragment key={email.meid}>
                        <tr className="hover:bg-gray-50">
                          <td className="max-w-md px-4 py-3">
                            <p className="line-clamp-1 font-medium text-gray-900">{email.subject}</p>
                            <p className="text-xs text-gray-400">
                              {email.from_mailbox} → {email.to_email}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{outletName(email.moid)}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {OUTLET_EMAIL_KIND_LABEL[email.kind] ?? email.kind}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {email.submitted_at
                              ? new Date(email.submitted_at).toLocaleString()
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex gap-1.5">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  setPreviewing(previewing === email.meid ? null : email.meid)
                                }
                              >
                                {previewing === email.meid ? 'Hide' : 'Read'}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                className="gap-1.5"
                                disabled={transitionEmail.isPending}
                                onClick={() => doApproveEmail(email)}
                              >
                                <Send className="h-3.5 w-3.5" /> Approve &amp; send
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {previewing === email.meid && (
                          <tr>
                            <td colSpan={5} className="bg-gray-50 px-4 py-3">
                              <div className="rounded-md border border-gray-200 bg-white">
                                <EmailEditor value={email.body} editable={false} minHeight={80} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
                  Approving sends the email immediately, from the mailbox shown, with that
                  mailbox's signature. The outlet's reply arrives in the Communications inbox.
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </MarketingPageShell>
  );
};

export default ApprovalsPage;
